import React from "react"

import {
	Box,
	Button,
	Grid,
	Stack,
	Tab,
	Typography,
} from "@mui/material"
import { ThemeProvider } from "@mui/material/styles"
import {
	TabContext,
	TabList,
	TabPanel,
} from "@mui/lab"

import {
	t,
	Trans,
} from "@lingui/macro"

import { UseFormReturn } from "react-hook-form"

import {
	Loading,
	MatesMultiSelect,
	MatesSelect,
	MatesTextField,
	withUseFormHook,
} from "@sinossi/mates-react-library"

import "yup.config"

import { darkTheme } from "commons/themes"
import { JobType } from "models/representative"
import { Brand } from "models/brand"
import { Company } from "models/company"
import { Product } from "models/product"
import { BrandProfileUpdateRequest } from "models/brand-profile-update"

import { CustomDropzoneLogo } from "../customDropzoneLogo"
import BrandSchema, { UpdateBrandForm } from "./form"
import { BrandRequisiteForm } from "components/brand/brand-form/brand-requisite-form"

interface BrandFormProps extends UseFormReturn
{
	brand: Brand | null
	companiesList: Company[]
	productsList: Product []
	submitForm: (data: BrandProfileUpdateRequest, logo: FormData | null) => void
}
interface BrandFormState
{
	isFormLoaded: boolean,
	disableStore: boolean
	disableLand: boolean
	storeSliderValues: number[]
	landSliderValues: number[]
	logo: FormData | null
	currentTab: string
}
class BrandForm extends React.Component<BrandFormProps, BrandFormState>
{
    constructor(props: BrandFormProps)
    {
        super(props)
		this.state = {
			isFormLoaded: false,
			disableStore: false,
			disableLand: false,
            storeSliderValues: [0, 0, ],
            landSliderValues: [0, 0, ],
			logo: null,
			currentTab: "GLOBAL",
		}
	}

	componentDidMount()
	{
		this.setFormValues()
	}

    private setFormValues = () : void => {
        if(this.props.brand === null) {
			return
		}

		const formData = UpdateBrandForm.fromServerResponse(this.props.brand)
		if(formData) {
			this.props.reset(formData as any)
		}

		this.setState(() => ({isFormLoaded: true}))
    }

	private productEntries = () => {
		const productEntries: Record<string, string> = {}
		for(const product of this.props.productsList) {
			productEntries[product.id] = product.name
		}
		return productEntries
	}

	private retrieveLogo = (data: FormData | null) => {
		this.setState(() => ({
			logo: data
		}))
	}

	private submitForm = (values: any): void => {
		const data: UpdateBrandForm = new UpdateBrandForm()
		Object.assign(data, values)

		const request: BrandProfileUpdateRequest = data.toRequest()
		this.props.submitForm(request, this.state.logo)
	}

	private onTabChange = (event: React.SyntheticEvent, newValue: string) => {
		this.setState(() => ({ currentTab: newValue }))
	}

	private onValidationFailed = (errors: any): void => {

		const errorKeys = Object.keys(errors)
		if(errorKeys[0].startsWith("requisites")) {
			switch(errorKeys[0]) {
				case "requisitesGeneric":
					this.setState(() => ({ currentTab: "GLOBAL" }))
					break
				case "requisitesUrban":
					this.setState(() => ({ currentTab: "URBAN" }))
					break
				case "requisitesSuburban":
					this.setState(() => ({ currentTab: "SUBURBAN" }))
					break
				case "requisitesHighStreet":
					this.setState(() => ({ currentTab: "HIGH_STREET" }))
					break
				case "requisitesMall":
					this.setState(() => ({ currentTab: "MALL" }))
					break
				case "requisitesRetailPark":
					this.setState(() => ({ currentTab: "RETAIL_PARK" }))
					break
				case "requisitesStandalone":
					this.setState(() => ({ currentTab: "STANDALONE" }))
					break
			}
		}
	}

    render()
	{
		const jobOptions = Object.values(JobType).map((job: any) => (
			<option key={job} value={job}>
				<Trans id = {`enums.representative-job.${job}`}>
				</Trans>
			</option>
		))

        return (
			<>
			<Loading show={!this.state.isFormLoaded} />
			{this.state.isFormLoaded &&
            <Box>
				<Grid container>
					<Grid item xs={12} md={4} textAlign="center" alignItems="center">
						<CustomDropzoneLogo logoImg={this.props.brand?.logo} callback={this.retrieveLogo}/>
					</Grid>
					<Grid item xs={12} md={8} sx={{bgcolor:"primary.main", color:"background.default"}} px={5}>
						<Stack justifyContent={"center"}  height={150} >
							<ThemeProvider theme={darkTheme}>
								<MatesTextField
									variant={'standard'}
									label={t({
										id: "brandProfile.brandName",
										message: "Nome",
									})}
									name="brandName"
									control={this.props.control}
									trigger={this.props.trigger}
									formState={this.props.formState}
									sx={() => {
										return {
											"& .MuiInputBase-root": {
												fontSize: "30px"
											}
										}
									}}
								/>
							</ThemeProvider>
						</Stack>
					</Grid>{/*Container logo e scritta*/}
				</Grid>
				<Box sx={{ bgcolor: 'background.default', pt: 2, px:2}}>
					<Grid container spacing={2}>
						<Grid item xs={12} md={4}>
							<Stack direction="column" spacing={3}>
								<Grid item xs={12}>
									<MatesTextField
										variant={"outlined"}
										label={t({
											id: "brandProfile.representative",
											message: "Referente",
										})}
										name="representative"
										control={this.props.control}
										trigger={this.props.trigger}
										formState={this.props.formState}
									/>
								</Grid>
								<Grid item xs={12}>
									<MatesTextField
										variant={"outlined"}
										label={t({
											id: "brandProfile.email",
											message: "Email",
										})}
										name="email"
										control={this.props.control}
										trigger={this.props.trigger}
										formState={this.props.formState}
									/>
								</Grid>
								<Grid item xs={12}>
									<MatesTextField
										variant={"outlined"}
										label={t({
											id: "brandProfile.phone",
											message: "Telefono",
										})}
										name="phone"
										control={this.props.control}
										trigger={this.props.trigger}
										formState={this.props.formState}
									/>
								</Grid>
								<Grid item xs={12}>
									<MatesSelect
										variant={"outlined"}
										label={t({
											id: "brandProfile.job",
											message: "Ruolo",
										})}
										name="job"
										control={this.props.control}
										trigger={this.props.trigger}
										formState={this.props.formState}
									>
										{jobOptions}
									</MatesSelect>
								</Grid>
							</Stack>
						</Grid>
						<Grid container item xs={12} md={8} rowSpacing={3} columnSpacing={2} alignItems="flex-start">
							<Grid item xs={12} md={6}>
								<MatesMultiSelect
									name="products"
									label={t({
										id: "brandProfile.merch",
										message: "Categorie merceologica",
									})}
									variant="outlined"
									control={this.props.control}
									formState={this.props.formState}
									trigger={this.props.trigger}
									entries={this.productEntries()}
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<MatesSelect
									variant={"outlined"}
									label={t({
										id: "brandProfile.company",
										message: "Azienda",
									})}
									name="company"
									control={this.props.control}
									trigger={this.props.trigger}
									formState={this.props.formState}
								>
									{this.props.companiesList.map((company) => (
										<option value={company.id} key={company.id}>{company.name}</option>
									))}
								</MatesSelect>
							</Grid>

							<Grid item xs={12} md={12}>
								<Typography sx={{ fontWeight: "600" }} color="primary">Requisiti</Typography>
								<TabContext value={this.state.currentTab}>
									<Box sx={{ borderBottom: 1, borderColor: "divider" }}>
										<TabList onChange={this.onTabChange} variant="scrollable" scrollButtons="auto">
											<Tab label="Globali" value="GLOBAL" wrapped sx={{maxWidth: "150px"}} />
											<Tab label="Urbano" value="URBAN" wrapped sx={{maxWidth: "150px"}} />
											<Tab label="Periferia" value="SUBURBAN" wrapped sx={{maxWidth: "150px"}} />
											<Tab label={<>Centro città /<br /> Strada principale</>} value="HIGH_STREET" wrapped sx={{maxWidth: "150px"}} />
											<Tab label="Centro commerciale" value="MALL" wrapped sx={{maxWidth: "150px"}} />
											<Tab label="Retail park" value="RETAIL_PARK" wrapped sx={{maxWidth: "150px"}} />
											<Tab label="Standalone" value="STANDALONE" wrapped sx={{maxWidth: "150px"}} />
										</TabList>
									</Box>
									<TabPanel value="GLOBAL">
										<BrandRequisiteForm
											formEntry="requisitesGeneric"
											{...this.props} />
									</TabPanel>
									<TabPanel value="URBAN">
										<BrandRequisiteForm
											formEntry="requisitesUrban"
											{...this.props} />
									</TabPanel>
									<TabPanel value="SUBURBAN">
										<BrandRequisiteForm
											formEntry="requisitesSuburban"
											{...this.props} />
									</TabPanel>
									<TabPanel value="HIGH_STREET">
										<BrandRequisiteForm
											formEntry="requisitesHighStreet"
											{...this.props} />
									</TabPanel>
									<TabPanel value="MALL">
										<BrandRequisiteForm
											formEntry="requisitesMall"
											{...this.props} />
									</TabPanel>
									<TabPanel value="RETAIL_PARK">
										<BrandRequisiteForm
											formEntry="requisitesRetailPark"
											{...this.props} />
									</TabPanel>
									<TabPanel value="STANDALONE">
										<BrandRequisiteForm
											formEntry="requisitesStandalone"
											{...this.props} />
									</TabPanel>
								</TabContext>
							</Grid>
						</Grid>

						<Grid container item textAlign="center" sx={{mt: 2, mb: 2}}>
							<Grid item xs={12} md={12}>
								<Button
									variant="contained"
									disableElevation
									type="button"
									onClick={this.props.handleSubmit(this.submitForm, this.onValidationFailed)}
									sx={{
										textTransform: 'none',
										backgroundColor: 'secondary.main',
										color: 'white',
										paddingX: 5,
										height: 28,
									}}
								>
									<Typography>
										<Trans id="brandProfile.save">
											Salva
										</Trans>
									</Typography>
								</Button>
							</Grid>
						</Grid>
					</Grid>
				</Box>
            </Box>
			}
			</>
        )
	}
}

export default withUseFormHook<UpdateBrandForm>(
	BrandForm,
	BrandSchema,
	new UpdateBrandForm()
)
